import React, { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Dropdown from "../common/Dropdown";
import Button from "../common/Button";
import Modal from "../common/Modal";
import DateRangePicker from "../common/DateRangePicker";
import { useAdGroupOptions } from "../../hooks/useAdGroupOptions";
import { useCreativesByAdGroup } from "../../hooks/useCreativesByAdGroup";
import { useCityOptions } from "../../hooks/useCityOptions";
import { useRegionOptions } from "../../hooks/useRegionOptions";

// Helper function to format date in local timezone (YYYY-MM-DD)
const formatDateLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// "2026-10-01" as local midnight. new Date("2026-10-01") is UTC midnight,
// i.e. 05:30 in IST, which never matches a calendar cell.
const parseDateLocal = (value: string): Date => {
  const match = /^(d{4})-(d{2})-(d{2})$/.exec(value);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  return new Date(value);
};

const creativeReportFilterSchema = z
  .object({
    adGroupId: z
      .string()
      .min(1, "Ad Group is required")
      .refine((val) => val.trim().length > 0, {
        message: "Please select a valid Ad Group",
      }),
    creativeId: z
      .string()
      .min(1, "Creative is required")
      .refine((val) => val.trim().length > 0, {
        message: "Please select a Creative",
      }),
    dateRange: z
      .object({
        startDate: z.date({ required_error: "Start date is required" }).nullable(),
        endDate: z.date({ required_error: "End date is required" }).nullable(),
      })
      .refine(
        (data) => data.startDate !== null,
        {
          message: "Start date is required",
          path: ["startDate"],
        }
      )
      .refine(
        (data) => data.endDate !== null,
        {
          message: "End date is required",
          path: ["endDate"],
        }
      )
      .refine(
        (data) => {
          if (data.startDate && data.endDate) {
            return data.startDate <= data.endDate;
          }
          return true;
        },
        {
          message: "Start date must be less than or equal to end date",
          path: ["dateRange"],
        }
      ),
    cityId: z.string().optional(),
    regionId: z.string().optional(),
  });

type CreativeReportFilterFormData = z.infer<typeof creativeReportFilterSchema>;

// Interface for initial data that can accept both old format (startDate/endDate) and new format (dateRange)
interface CreativeReportFilterInitialData {
  adGroupId?: string;
  creativeId?: string;
  startDate?: string; // Old format - for backward compatibility
  endDate?: string; // Old format - for backward compatibility
  dateRange?: { startDate: Date | null; endDate: Date | null }; // New format
  cityId?: string;
  regionId?: string;
}

interface CreativeReportFilterModalProps {
  visible: boolean;
  initialData?: CreativeReportFilterInitialData;
  onSubmit: (data: CreativeReportFilterFormData) => void;
  onCancel: () => void;
  onClose: () => void;
  isLoading?: boolean;
  // Optional callback to clear applied filters in parent while keeping the modal open
  onReset?: () => void;
}

const CreativeReportFilterModal: React.FC<CreativeReportFilterModalProps> = ({
  visible,
  initialData,
  onSubmit,
  onCancel,
  onClose,
  isLoading = false,
  onReset,
}) => {
  const {
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
    trigger,
  } = useForm<CreativeReportFilterFormData>({
    resolver: zodResolver(creativeReportFilterSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: initialData,
  });

  const selectedAdGroupId = watch("adGroupId");
  const selectedCityId = watch("cityId");

  const { adGroupOptions, isAdGroupOptionsLoading } = useAdGroupOptions();
  const { creativeOptions, isLoading: isCreativesLoading } =
    useCreativesByAdGroup(selectedAdGroupId || "");
  const { cityOptions, isCityOptionsLoading } = useCityOptions();
  const { regionOptions, isRegionOptionsLoading } = useRegionOptions(
    selectedCityId || undefined
  );

  // Track the initial values to detect user changes vs restoring from initialData
  const initialAdGroupIdRef = useRef<string | undefined>(
    initialData?.adGroupId
  );
  const initialCityIdRef = useRef<string | undefined>(initialData?.cityId);

  useEffect(() => {
    if (initialData) {
      // Convert startDate and endDate strings to dateRange object if they exist
      // This handles backward compatibility with the old format
      const convertedData: Partial<CreativeReportFilterFormData> = {
        adGroupId: initialData.adGroupId,
        creativeId: initialData.creativeId,
        cityId: initialData.cityId,
        regionId: initialData.regionId,
      };

      // If dateRange is already provided, use it
      if (initialData.dateRange) {
        convertedData.dateRange = initialData.dateRange;
      } 
      // Otherwise, convert startDate/endDate strings to dateRange
      else if (initialData.startDate || initialData.endDate) {
        convertedData.dateRange = {
          startDate: initialData.startDate
            ? parseDateLocal(initialData.startDate)
            : null,
          endDate: initialData.endDate
            ? parseDateLocal(initialData.endDate)
            : null,
        };
      }

      reset(convertedData);
      // Update refs when initialData changes
      initialAdGroupIdRef.current = initialData.adGroupId;
      initialCityIdRef.current = initialData.cityId;
    }
  }, [initialData, reset]);

  const handleReset = () => {
    reset({});
    // Reset the refs as well
    initialAdGroupIdRef.current = undefined;
    initialCityIdRef.current = undefined;
    // Let parent clear its applied filters but keep modal open
    onReset?.();
  };

  const handleFormSubmit = (data: CreativeReportFilterFormData) => {
    // Convert dateRange to startDate and endDate strings (YYYY-MM-DD format)
    const formValues = {
      adGroupId: data.adGroupId,
      creativeId: data.creativeId,
      startDate: data.dateRange?.startDate
        ? formatDateLocal(data.dateRange.startDate)
        : undefined,
      endDate: data.dateRange?.endDate
        ? formatDateLocal(data.dateRange.endDate)
        : undefined,
      cityId: data.cityId,
      regionId: data.regionId,
    };
    onSubmit(formValues as any);
  };

  return (
    <Modal isOpen={visible} onClose={onClose} title="Filters" size="full">
      {/* Every dropdown here is searchable: each is fed by an unpaginated
          options query, so the lists are as long as the tenant's data and
          render into a max-h-60 scroll box. */}
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left Column - Date Range Picker */}
          <div className="space-y-4">
            <DateRangePicker
              label="Date Range *"
              name="dateRange"
              error={errors.dateRange}
              disableFutureDates={true}
              value={watch("dateRange") ?? null}
              onChange={(value) => {
                setValue("dateRange", value, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              }}
            />
          </div>

          {/* Right Column - Other Inputs and Dropdowns */}
          <div className="space-y-4">
            <Dropdown
              label="Ad Group *"
              options={[...adGroupOptions, { value: "default", label: "Default" }]}
              value={watch("adGroupId") || ""}
              name="adGroupId"
              onChange={(val) => {
                const newAdGroupId = val as string;
                setValue("adGroupId", newAdGroupId, { shouldValidate: false });
                // Only reset creative if user changed adGroup (not when restoring from initialData)
                if (initialAdGroupIdRef.current !== newAdGroupId) {
                  setValue("creativeId", "", { shouldValidate: false });
                }
              }}
              onBlur={() => trigger("adGroupId")}
              error={errors.adGroupId}
              disabled={isAdGroupOptionsLoading}
              searchable
              placeholder="Select Ad Group"
            />
            <Dropdown
              label="Creative *"
              options={creativeOptions}
              value={watch("creativeId") || ""}
              name="creativeId"
              onChange={(val) => setValue("creativeId", val as string, { shouldValidate: false })}
              onBlur={() => trigger("creativeId")}
              error={errors.creativeId}
              disabled={!selectedAdGroupId || isCreativesLoading}
              searchable
              placeholder={
                !selectedAdGroupId ? "Select Ad Group first" : "Select Creative"
              }
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Dropdown
                label="City"
                options={cityOptions}
                value={watch("cityId") || ""}
                name="cityId"
                onChange={(val) => {
                  const newCityId = val as string;
                  setValue("cityId", newCityId, { shouldValidate: false });
                  // Only reset region if user changed city (not when restoring from initialData)
                  if (initialCityIdRef.current !== newCityId) {
                    setValue("regionId", "", { shouldValidate: false });
                  }
                }}
                onBlur={() => trigger("cityId")}
                error={errors.cityId}
                disabled={isCityOptionsLoading}
                searchable
                placeholder="Select City (Optional)"
              />
              <Dropdown
                label="Region"
                options={regionOptions}
                value={watch("regionId") || ""}
                name="regionId"
                onChange={(val) => setValue("regionId", val as string, { shouldValidate: false })}
                onBlur={() => trigger("regionId")}
                error={errors.regionId}
                disabled={!selectedCityId || isRegionOptionsLoading}
                searchable
                placeholder={
                  !selectedCityId ? "Select City first" : "Select Region (Optional)"
                }
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-4 pt-4">
          <Button
            type="button"
            variant="secondary"
            onClick={handleReset}
            disabled={isLoading}
          >
            Reset
          </Button>
          <Button
            type="submit"
            variant="primary"
            isLoading={isLoading}
            className="w-full sm:w-auto"
          >
            Generate Report
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreativeReportFilterModal;
